import { Component, EventEmitter, inject, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import { GalaxyAvatarModule } from '@vendasta/galaxy/avatar';
import { MatListModule, MatSelectionList, MatSelectionListChange } from '@angular/material/list';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { ReviewSourceInfoMap, ReviewSources } from '../types';
import { debounceTime, distinctUntilChanged, map, startWith } from 'rxjs/operators';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import { GalaxyFormFieldModule } from '@vendasta/galaxy/form-field';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';

@Component({
  selector: 'reviews-source-selection-panel',
  imports: [
    CommonModule,
    GalaxyAvatarModule,
    MatListModule,
    MatButtonModule,
    MatIconModule,
    TranslateModule,
    GalaxyFormFieldModule,
    ReactiveFormsModule,
    MatInputModule,
  ],
  templateUrl: './source-selection-panel.component.html',
  styleUrls: ['./source-selection-panel.component.scss', '../source-selection-shared-styles.scss'],
  animations: [
    trigger('slideUpDown', [
      state(
        'open',
        style({
          height: '100%',
        }),
      ),
      state(
        'closed',
        style({
          height: '0',
        }),
      ),
      transition('open <=> closed', [animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')]),
    ]),
  ],
})
export class SourceSelectionPanelComponent implements OnInit, OnDestroy {
  @Input() reviewSources: ReviewSources;
  @Input() openWithSelectedSources$: Observable<string[]>;
  @Input() headerTranslationKey: string;
  @Input() maxSelections = Infinity;
  @Input() keepOpen? = false;
  @Output() sourceIdsSelected = new EventEmitter<string[] | null>();

  @ViewChild('sourceSelectionList') sourceSelectionList: MatSelectionList;
  opened$$ = new Subject<boolean>();
  searchQueryControl = new FormControl('');
  filteredReviewSources$: Observable<ReviewSources>;
  selectedSourceIds: Set<string> = new Set();
  preselectedSourceIds: string[] = [];

  private snackbarService = inject(SnackbarService);
  private translateService = inject(TranslateService);

  private subscriptions: Subscription[] = [];

  ngOnInit(): void {
    const searchQueryChanged$ = this.searchQueryControl.valueChanges.pipe(
      debounceTime(300),
      distinctUntilChanged(),
      startWith(''),
    );

    this.filteredReviewSources$ = searchQueryChanged$.pipe(
      map((searchQuery) => {
        if (!searchQuery) {
          return { ...this.reviewSources };
        }
        return {
          primarySourceIds: this.filterSources(
            searchQuery,
            this.reviewSources.primarySourceIds,
            this.reviewSources.sourceInfo,
          ),
          otherSourceIds: this.filterSources(
            searchQuery,
            this.reviewSources.otherSourceIds,
            this.reviewSources.sourceInfo,
          ),
          sourceInfo: this.reviewSources.sourceInfo,
        };
      }),
    );

    this.subscriptions.push(
      this.openWithSelectedSources$.subscribe((preselectedSources) => {
        this.preselectedSourceIds = preselectedSources;
        // Clear the selectedSourceIds set and add the preselected sources to it
        this.selectedSourceIds.clear();
        preselectedSources.forEach((sourceId) => this.selectedSourceIds.add(sourceId));

        // Set the selected state of each option in the list
        this.sourceSelectionList.options.forEach((option) => {
          const sourceId = option.value;
          option.selected = this.selectedSourceIds.has(sourceId);
        });

        // Reset the search and open the panel
        this.searchQueryControl.setValue('');
        this.opened$$.next(true);
      }),
    );
  }

  filterSources(searchQuery: string, sourceIds: string[], reviewSourceInfoMap: ReviewSourceInfoMap): string[] {
    return sourceIds.filter((sourceId) =>
      reviewSourceInfoMap[sourceId].sourceName.toLowerCase().includes(searchQuery.toLowerCase()),
    );
  }

  onSelectionChange($event: MatSelectionListChange) {
    $event.options.forEach((option) => {
      const sourceId = option.value;
      if (option.selected) {
        this.selectedSourceIds.add(sourceId);
      } else {
        this.selectedSourceIds.delete(sourceId);
      }
    });
  }

  onCancel(): void {
    this.opened$$.next(false);
    this.sourceIdsSelected.next(this.selectedSourceIds.size ? this.preselectedSourceIds : null);
  }

  onSave(): void {
    const selectedSourceIds = [...this.selectedSourceIds];

    // Check appropriate number of sources is selected
    if (selectedSourceIds.length === 0) {
      this.snackbarService.openErrorSnack(
        this.translateService.instant('REVIEW_SOURCE_SELECTION.MESSAGES.ERROR_NO_SOURCE_SELECTED'),
      );
      return;
    } else if (selectedSourceIds.length > this.maxSelections) {
      this.snackbarService.openErrorSnack(
        this.translateService.instant('REVIEW_SOURCE_SELECTION.MESSAGES.ERROR_SELECT_LESS_SOURCES_THAN_LIMIT', {
          maxSelections: this.maxSelections,
        }),
      );
      return;
    }

    // Close the panel and emit the values
    this.opened$$.next(false);
    this.sourceIdsSelected.next(selectedSourceIds);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }
}
